import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Compass } from "lucide-react";
import { toast } from "sonner";
import { submitEmailSignup } from "@/lib/api";
import { VipMemberCard } from "@/components/VipMemberCard";
import { setMyMemberNumber } from "@/lib/referral";

const BENEFITS = [
  "VIP Member Number",
  "Founder Journal Updates",
  "Behind-the-Scenes Photos & Videos",
  "Prototype Progress",
  "Early Access Before Public Launch",
];

/**
 * JoinJourneyCard — premium "Join the Journey" CTA shown at the bottom
 * of each /journal/:slug page (and reusable elsewhere). On success it
 * swaps in the VipMemberCard so the rider sees their founding number
 * inline without leaving the entry.
 */
export const JoinJourneyCard = ({ source = "journal-entry" }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [member, setMember] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const data = await submitEmailSignup(email, source);
      setMyMemberNumber(data.member_number);
      setMember({
        memberNumber: data.member_number,
        email: data.email,
        resubscribed: !!data.resubscribed,
      });
      toast.success(
        data.resubscribed
          ? "Welcome back to the VIP Crew."
          : `You're in! Your VIP number is #${String(data.member_number).padStart(3, "0")} — ride safe, we'll be in touch soon.`
      );
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't join right now. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (member) {
    return (
      <motion.div
        data-testid="join-journey-success"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
      >
        <div className="text-center mb-6">
          <div className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold mb-2">
            YOU&rsquo;RE IN
          </div>
          <h3 className="h-display text-white text-3xl sm:text-4xl">
            Welcome to the <span className="text-[#F47A20]">journey</span>.
          </h3>
        </div>
        <VipMemberCard
          memberNumber={member.memberNumber}
          email={member.email}
          resubscribed={member.resubscribed}
        />
      </motion.div>
    );
  }

  return (
    <motion.section
      data-testid="join-journey-card"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      className="relative overflow-hidden bg-[#0E0E11] border border-[#F47A20]/25 rounded-sm"
    >
      {/* Subtle orange edge accents */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#F47A20]/60 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#F47A20]/30 to-transparent" />
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F47A20]/[0.07] blur-3xl" />
      {/* Grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-7 md:p-10">
        {/* Left — copy */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mb-5 px-2.5 py-1 border border-[#F47A20]/35 bg-[#F47A20]/8 rounded-sm">
            <Compass size={12} className="text-[#F47A20]" />
            <span className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold">
              THE ROADRIG JOURNEY
            </span>
          </div>

          <h2
            data-testid="join-journey-headline"
            className="h-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-5"
          >
            Join the <span className="text-[#F47A20]">Journey</span>.
          </h2>

          <p className="text-white/72 text-base md:text-lg leading-relaxed mb-3 max-w-xl">
            You&rsquo;re reading the real story behind ROADRIG and the OUTLAW V1.
          </p>
          <p className="text-white/72 text-base md:text-lg leading-relaxed mb-7 max-w-xl">
            Follow every milestone &mdash; from prototype development to road testing and launch &mdash; and be among the first riders to experience what&rsquo;s coming.
          </p>

          <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl">
            <input
              data-testid="join-journey-input"
              type="email"
              required
              placeholder="rider@roadrig.com.au"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-[#0A0A0C] border border-white/12 rounded-sm px-4 py-3.5 text-white placeholder:text-white/30 focus:border-[#F47A20] focus:ring-1 focus:ring-[#F47A20] outline-none transition-colors"
            />
            <button
              data-testid="join-journey-submit"
              type="submit"
              disabled={submitting}
              className="btn-primary justify-center whitespace-nowrap"
            >
              {submitting ? "Joining..." : "Join the ROADRIG Journey"}
              <ArrowRight size={16} />
            </button>
          </form>

          <p className="serial text-[10px] text-white/40 mt-5">
            No spam. Quiet updates from the workshop floor &mdash; only when there&rsquo;s something real to share.
          </p>
        </div>

        {/* Right — benefits */}
        <div className="lg:col-span-5 lg:border-l lg:border-white/8 lg:pl-12">
          <div className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold mb-5">
            WHAT YOU GET
          </div>
          <ul data-testid="join-journey-benefits" className="space-y-3.5">
            {BENEFITS.map((b, i) => (
              <li
                key={b}
                data-testid={`join-journey-benefit-${i + 1}`}
                className="flex items-start gap-3 text-white/85"
              >
                <span className="serial text-[10px] text-[#F47A20] font-bold mt-1.5 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-sm md:text-[15px] leading-snug">{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 pt-5 border-t border-white/8">
            <p className="serial text-[10px] text-white/45 leading-relaxed">
              Founding members get a permanent VIP number. When pre-orders open, you go in number order &mdash; not last in line.
            </p>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default JoinJourneyCard;
